import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

type FilterValues = Record<string, string | string[]>

/**
 * Filters live in the URL (§8.3): a view can be bookmarked and sent to someone
 * else, and the back button does what one expects.
 *
 * `defaults` describes the shape of the filter set -- which keys exist and
 * which of them hold several values -- so it is read once and frozen. That is
 * what makes the memo below correct without comparing the object on every
 * render: callers pass an object literal, but its content never changes.
 */
export function useUrlFilters<T extends FilterValues>(
  defaults: T,
): [T, (changes: Partial<T>) => void, () => void] {
  const shape = useRef(defaults).current
  const [params, setParams] = useSearchParams()

  const filters = useMemo(() => {
    const out = { ...shape }
    for (const key of Object.keys(shape)) {
      const values = params.getAll(key)
      if (values.length === 0) continue
      ;(out as FilterValues)[key] = Array.isArray(shape[key]) ? values : values[0]
    }
    return out
  }, [params, shape])

  // The functional form keeps this callback stable and, more to the point, free
  // of a stale copy of the search params: two updates in the same tick would
  // otherwise overwrite each other. `replace` keeps every keystroke out of the
  // history.
  const update = useCallback(
    (changes: Partial<T>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          for (const [key, value] of Object.entries(changes)) {
            next.delete(key)
            if (Array.isArray(value)) value.forEach((one) => one && next.append(key, one))
            else if (value) next.set(key, String(value))
          }
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  // Clearing does push an entry: going back after an accidental reset brings
  // the filters back, which is the one moment history is worth having.
  const reset = useCallback(() => setParams(new URLSearchParams()), [setParams])

  return [filters, update, reset]
}
